import React, { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { apiFetch } from "../api/apiFetch";
import { getViewAs, onViewAsChange, stopViewAs } from "../utils/viewAs";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [viewAs, setViewAs] = useState(getViewAs);

  useEffect(() => onViewAsChange(() => setViewAs(getViewAs())), []);

  // "Bekijk als" is voorbehouden aan wie gebruikers beheert; zonder dat recht (of na uitloggen) vervalt het.
  useEffect(() => {
    if (viewAs && (!user ? !loading : !permissions.includes("users.manage") && permissions.length > 0)) stopViewAs();
  }, [viewAs, user, loading, permissions]);

  useEffect(() => {
    // Bij het laden vuren getSession en onAuthStateChange vlak na elkaar: één profielaanvraag volstaat.
    const recent = { id: null, at: 0, promise: null };
    const fetchUserProfile = (authUser) => {
      if (recent.promise && recent.id === authUser.id && Date.now() - recent.at < 5000) {
        return recent.promise.then((loaded) => ({
          ...authUser,
          first_name: loaded.first_name,
          last_name: loaded.last_name,
          displayName: loaded.displayName,
          permissions: loaded.permissions,
        }));
      }
      const promise = loadUserProfile(authUser);
      Object.assign(recent, { id: authUser.id, at: Date.now(), promise });
      promise.catch(() => { recent.promise = null; });
      return promise;
    };

    const loadUserProfile = async (authUser) => {
      try {
        const profile = await apiFetch("/users/me/profile");
        const userPermissions = profile.permissions || [];
        setPermissions(userPermissions);

        return {
          ...authUser,
          first_name: profile.first_name,
          last_name: profile.last_name,
          displayName: profile.first_name || profile.last_name || authUser.email,
          permissions: userPermissions,
        };
      } catch (err) {
        console.error("[AuthContext] Error fetching profile:", err);

        if (err.message && err.message.includes("403")) {
          await supabase.auth.signOut();
          throw new Error("User not authorized");
        }

        setPermissions([]);
        return {
          ...authUser,
          first_name: null,
          last_name: null,
          displayName: authUser.email,
          permissions: [],
        };
      }
    };

    const initAuth = async () => {
      try {
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error("getSession timeout")), 15000)
        );

        const { data: { session } } = await Promise.race([sessionPromise, timeoutPromise]);

        if (session?.user) {
          try {
            const userWithProfile = await fetchUserProfile(session.user);
            setUser(userWithProfile);
          } catch (err) {
            if (err.message === "User not authorized") {
              setIsUnauthorized(true);
              setUser(null);
              setPermissions([]);
            } else {
              setUser({
                ...session.user,
                first_name: null,
                last_name: null,
                displayName: session.user.email,
                permissions: [],
              });
            }
          }
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error("[AuthContext] Init error:", error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    const { data: listener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (session?.user) {
          try {
            const userWithProfile = await fetchUserProfile(session.user);
            setUser(userWithProfile);
          } catch (err) {
            if (err.message === "User not authorized") {
              setIsUnauthorized(true);
              setUser(null);
              setPermissions([]);
            } else {
              setUser({
                ...session.user,
                first_name: null,
                last_name: null,
                displayName: session.user.email,
                permissions: [],
              });
            }
          }
        } else {
          setUser(null);
          setPermissions([]);
        }
        setLoading(false);
      }
    );

    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  const logout = async () => {
    stopViewAs();
    await supabase.auth.signOut();
    setUser(null);
    setPermissions([]);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ user, loading, logout, permissions: viewAs ? viewAs.permissions : permissions, realPermissions: permissions, viewAs, isUnauthorized }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
